/**
 * 指挥页队员标记上图编排（从 Dashboard.updateMarkers 拆出）
 */
import mapboxgl from 'mapbox-gl';
import {
  resolveMemberMarkerStyle,
  createAccuracyCircleElement,
  createFlashlightElement,
  createTeardropMarkerElement,
  createMemberNameLabelElement
} from './memberMarkers';
import { getMemberDisplayName, getMemberSourceBadge } from './deviceIdentity';
import { toMapLngLat, getActiveBasemapIdForCoord } from './basemapCoord';
import { bindMemberMarkerInteractions } from './memberMarkerPopupHelpers';
import { ONLINE_TIMEOUT, DEFAULT_OFFLINE_TIMEOUT } from './constants';

/** Mapbox 自定义事件：地球俯冲/弃全景切回 mercator 后触发，供队员 HTML 标记补画 */
export const MEMBER_MARKERS_PROJECTION_READY_EVENT = 'zhxt-member-markers-projection-ready';

function removeMarkerSafe(marker) {
  try {
    const popups = marker?.__zhxtMemberPopups;
    if (popups) {
      try { popups.hoverPopup?.remove(); } catch (_) { /* ignore */ }
      try { popups.actionPopup?.remove(); } catch (_) { /* ignore */ }
      popups.closeHover?.();
    }
  } catch (_) { /* ignore */ }
  try { marker?.remove(); } catch (_) { /* ignore */ }
}

export function readProjectionName(map) {
  try {
    const proj = map?.getProjection?.();
    if (typeof proj === 'string') return proj;
    return proj?.name || 'mercator';
  } catch (_) {
    return 'mercator';
  }
}

function resolveMembersForRedraw(currentMembers, options) {
  try {
    if (typeof options?.getMembers === 'function') {
      const list = options.getMembers();
      if (Array.isArray(list)) return list;
    }
  } catch (_) { /* ignore */ }
  return Array.isArray(currentMembers) ? currentMembers : [];
}

function rememberMarkerOptions(map, options) {
  if (!map || !options) return;
  map.__zhxtMemberMarkerLastOptions = options;
}

function membersHaveDrawableCoords(members) {
  return (members || []).some((m) => {
    if (!m) return false;
    // 与绘制循环一致：缺省/0 不上图；避免把合法小数值误判（经纬度 0 本就几乎不用）
    return !!(m.last_longitude && m.last_latitude);
  });
}

/**
 * 离开 globe 后补画。注意：
 * - Mapbox setProjection('mercator') 后 getProjection 可能短暂仍回报 globe → notify 时不可直接弃单
 * - notify / 俯冲结束时 getMembers 可能仍为空 → 不可 done 掉，须等成员到达后再画
 * - 否则只能靠 presence 5s 或 WS 晚到才显示，表现为「过很久才出 / 再刷又没有」
 */
function scheduleMemberMarkersWhenMercator(map, currentMembers, options) {
  if (!map) return;
  rememberMarkerOptions(map, options);
  if (map.__zhxtMemberMarkerGlobeRetry) return;
  map.__zhxtMemberMarkerGlobeRetry = true;

  let done = false;
  let notifyRetryTimer = null;

  const cleanup = () => {
    map.__zhxtMemberMarkerGlobeRetry = false;
    try { map.off?.('moveend', onPoll); } catch (_) { /* ignore */ }
    try { map.off?.('zoomend', onPoll); } catch (_) { /* ignore */ }
    try { map.off?.('idle', onPoll); } catch (_) { /* ignore */ }
        try { map.off?.(MEMBER_MARKERS_PROJECTION_READY_EVENT, onNotify); } catch (_) { /* ignore */ }
    clearInterval(poll);
    clearTimeout(safetyTimer);
    if (notifyRetryTimer) clearTimeout(notifyRetryTimer);
    notifyRetryTimer = null;
  };

  const tryDraw = () => {
    const members = resolveMembersForRedraw(currentMembers, options);
    if (!membersHaveDrawableCoords(members)) {
      // 仍无坐标：保持监听，等 getMembers / 后续 updateMarkers 灌数
      return false;
    }
    done = true;
    cleanup();
    try {
      updateMemberMarkersOnMap(map, members, options);
    } catch (error) {
      console.warn('离开地球投影后补画队员标记失败', error);
    }
    return true;
  };

  const onPoll = () => {
    if (done) return;
    if (readProjectionName(map) === 'globe') return;
    tryDraw();
  };

  const onNotify = () => {
    if (done) return;
    // 投影切换可能滞后：短重试，绝不因一次仍报 globe 就弃单
    let attempts = 0;
    const tick = () => {
      if (done) return;
      attempts += 1;
      if (readProjectionName(map) === 'globe') {
        if (attempts < 30) {
          notifyRetryTimer = setTimeout(tick, 50);
          return;
        }
        // ~1.5s 仍报 globe：不 cleanup，留给 poll / 下次 notify
        return;
      }
      tryDraw();
    };
    tick();
  };

  try { map.on?.('moveend', onPoll); } catch (_) { /* ignore */ }
  try { map.on?.('zoomend', onPoll); } catch (_) { /* ignore */ }
  try { map.on?.('idle', onPoll); } catch (_) { /* ignore */ }
    try { map.on?.(MEMBER_MARKERS_PROJECTION_READY_EVENT, onNotify); } catch (_) { /* ignore */ }

  const poll = setInterval(onPoll, 250);
  // 安全阀：到点再试；无论成败都卸监听，避免永久 setInterval。之后靠 updateMarkers / 再次 notify 重挂。
  const safetyTimer = setTimeout(() => {
    if (done) return;
    if (readProjectionName(map) !== 'globe') {
      tryDraw();
    }
    if (!done) {
      // 卸监听避免永久 setInterval；不清空已有点（globe 路径已改为 defer 前不 clear）。
      // 下次 updateMarkers / notify 会重新 schedule。
      console.warn('队员标记 globe 补画等待超时，保留现有点并等待下次投影就绪/刷新');
      done = true;
      cleanup();
    }
  }, 20000);
}

/**
 * 地球俯冲结束 / 弃全景切 mercator 后调用。
 * 即使当时尚未 schedule（队员晚到），也会用 map 上缓存的 lastOptions 直接尝试补画。
 */
export function notifyMemberMarkersProjectionReady(map) {
  if (!map) return;

  // 已有 globe 补画监听：只唤醒，避免与直连路径双画
  if (map.__zhxtMemberMarkerGlobeRetry) {
    try {
      map.fire?.(MEMBER_MARKERS_PROJECTION_READY_EVENT);
    } catch (_) { /* ignore */ }
    return;
  }

  // notify 早于任何 schedule（俯冲先结束、队员后到 / 或上次弃单）：用缓存 options 直连或挂起
  const options = map.__zhxtMemberMarkerLastOptions;
  if (!options || !options.markersRef) {
    try {
      map.fire?.(MEMBER_MARKERS_PROJECTION_READY_EVENT);
    } catch (_) { /* ignore */ }
    return;
  }

  let attempts = 0;
  const tick = () => {
    attempts += 1;
    if (readProjectionName(map) === 'globe') {
      if (attempts < 30) {
        setTimeout(tick, 50);
        return;
      }
      // 仍报 globe：挂起等待，后续 projection/poll 再画
      scheduleMemberMarkersWhenMercator(map, [], options);
      return;
    }
    const members = resolveMembersForRedraw([], options);
    if (!membersHaveDrawableCoords(members)) {
      scheduleMemberMarkersWhenMercator(map, members, options);
      return;
    }
    try {
      updateMemberMarkersOnMap(map, members, options);
    } catch (error) {
      console.warn('投影就绪后补画队员标记失败', error);
    }
  };
  tick();
}

/**
 * @param {mapboxgl.Map} map
 * @param {Array} currentMembers
 * @param {{
 *   markersRef: { current: any[] },
 *   activeMemberId?: string|null,
 *   highlightedMemberIds?: Set<string>,
 *   showMemberNames?: boolean,
 *   onlineTimeoutMs?: number,
 *   offlineTimeoutMs?: number,
 *   presenceById?: object|null,
 *   speedAuto?: boolean,
 *   getMembers?: () => Array,
 *   onFlyTo?: (member: object) => void,
 *   onRemoveFromMap?: (member: object) => void,
 *   onOpenChat?: (member: object) => void
 * }} options
 */
export function updateMemberMarkersOnMap(map, currentMembers, options = {}) {
  if (!map) return;
  rememberMarkerOptions(map, options);


  const {
    markersRef,
    activeMemberId = null,
    highlightedMemberIds = new Set(),
    showMemberNames = false,
    onlineTimeoutMs = ONLINE_TIMEOUT,
    offlineTimeoutMs = DEFAULT_OFFLINE_TIMEOUT,
    presenceById = null,
    speedAuto = true,
    onFlyTo,
    onRemoveFromMap,
    onOpenChat
  } = options;

  if (!markersRef) return;

  // CRITICAL: HTML Markers don't position correctly in globe projection.
  // 先 defer、后清空：若在 globe 等待期先 clear，presence/筛选每次刷新都会把已有点抹掉，
  // 安全阀弃单后只能干等下次 update，表现为「经常不显示、说不清何时才出」。
  const projection = readProjectionName(map);
  if (projection === 'globe') {
    scheduleMemberMarkersWhenMercator(map, currentMembers, options);
    return;
  }

  markersRef.current.forEach((marker) => removeMarkerSafe(marker));
  markersRef.current = [];
  const now = Date.now();

  (currentMembers || []).forEach((member) => {
    if (!member.last_longitude || !member.last_latitude) return;

    const displayLngLat = toMapLngLat(
      member.last_longitude,
      member.last_latitude,
      getActiveBasemapIdForCoord(map),
      map
    );
    if (!displayLngLat) return;

    const memberId = member.device_unique_id || member.name;
    const displayName = getMemberDisplayName(member);
    const sourceBadge = getMemberSourceBadge(member);
    const { id, isOnline, fromApp, appPinColor, iconKey } = resolveMemberMarkerStyle(member, {
      activeMemberId,
      highlightedMemberIds,
      now,
      onlineTimeoutMs,
      offlineTimeoutMs,
      presenceOverride: presenceById && memberId ? presenceById[memberId] : null,
      speedAuto
    });

    if (isOnline && member.last_accuracy != null && member.last_accuracy > 0) {
      const accuracySvg = createAccuracyCircleElement(member.last_accuracy);
      const accuracyMarker = new mapboxgl.Marker({
        element: accuracySvg,
        anchor: 'center'
      })
        .setLngLat(displayLngLat)
        .addTo(map);
      accuracyMarker.getElement().style.pointerEvents = 'none';
      markersRef.current.push(accuracyMarker);
    }

    if (isOnline && member.last_bearing != null) {
      const flashlightSvg = createFlashlightElement(member.last_bearing || 0, id);
      const flashlightMarker = new mapboxgl.Marker({
        element: flashlightSvg,
        anchor: 'center'
      })
        .setLngLat(displayLngLat)
        .addTo(map);
      flashlightMarker.getElement().style.pointerEvents = 'none';
      markersRef.current.push(flashlightMarker);
    }

    try {
      const el = createTeardropMarkerElement({
        pinColor: appPinColor,
        title: sourceBadge === 'app'
          ? `${displayName}（单兵 App）`
          : `${displayName}（Locus）`,
        iconKey,
        fromApp: sourceBadge === 'app',
        cornerBadge: sourceBadge
      });

      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'bottom',
        pitchAlignment: 'viewport',
        rotationAlignment: 'viewport',
        offset: [0, 0]
      })
        .setLngLat(displayLngLat)
        .addTo(map);

      bindMemberMarkerInteractions(map, marker, el, member, displayLngLat, {
        onFlyTo,
        onRemoveFromMap,
        onOpenChat
      });

      markersRef.current.push(marker);

      if (showMemberNames && displayName) {
        const nameOffsetY = sourceBadge === 'app' ? -48 : -42;
        const nameMarker = new mapboxgl.Marker({
          element: createMemberNameLabelElement(displayName),
          anchor: 'bottom'
        })
          .setLngLat(displayLngLat)
          .setOffset([0, nameOffsetY])
          .addTo(map);
        nameMarker.getElement().style.pointerEvents = 'none';
        markersRef.current.push(nameMarker);
      }
    } catch (error) {
      console.warn('绘制队员标记失败', memberId || displayName, error);
    }
  });
}
